import axios from 'axios';
import { toast } from 'react-toastify';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8031";

// Create axios instance with base URL
const api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json'
    },
    withCredentials: true // This is crucial for cookies to be sent/received
});

// Request interceptor - Log requests in development & attach Bearer token
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        if (process.env.NODE_ENV === 'development') {
            console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`);
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Error code to user-friendly message mapping
const errorMessageMap = {
    // Auth Errors
    'INVALID_CREDENTIALS': 'Invalid email or password',
    'DUPLICATE_USER': 'An account with this email already exists',
    'USER_NOT_FOUND': 'No account found with this email',
    'WEAK_PASSWORD': 'Password must be at least 8 characters with uppercase, lowercase, number and special character',
    'INVALID_OTP': 'Invalid OTP code',
    'OTP_EXPIRED': 'OTP has expired. Please request a new one',
    'TOKEN_EXPIRED': 'Session expired. Please login again',
    'TOKEN_INVALID': 'Invalid session. Please login again',
    'UNAUTHORIZED': 'Please login to continue',
    'FORBIDDEN': 'You do not have permission to access this resource',

    // Validation
    'VALIDATION_ERROR': 'Please check your input',
    'MISSING_FIELDS': 'Please fill in all required fields',
    'INVALID_EMAIL': 'Please enter a valid email address',
    'INVALID_PHONE': 'Please enter a valid 10-digit phone number',

    // Resources
    'NOT_FOUND': 'Resource not found',
    'ALREADY_EXISTS': 'This item already exists',

    // Upload
    'UPLOAD_FAILED': 'Failed to upload file. Please try again',
    'INVALID_FILE_TYPE': 'Invalid file type. Please upload a valid image',
    'FILE_TOO_LARGE': 'File is too large. Maximum size is 5MB',

    // Booking/Cart
    'SLOT_UNAVAILABLE': 'This time slot is already booked',
    'ITEM_NOT_FOUND': 'Item not found',
    'DUPLICATE_BOOKING': 'You already have a booking for this service',

    // Server
    'DATABASE_ERROR': 'Database error. Please try again',
    'SERVER_ERROR': 'Server error. Please try again later',
    'NETWORK_ERROR': 'Network error. Please check your connection',
    'TIMEOUT': 'Request timed out. Please try again',
    'EMAIL_SEND_FAILED': 'Failed to send email. Please try again',
};

// Response interceptor for handling errors
api.interceptors.response.use(
    (response) => {
        if (process.env.NODE_ENV === 'development') {
            console.log(`[API Response] ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`);
        }
        return response;
    },
    (error) => {
        // Log error in development
        if (process.env.NODE_ENV === 'development') {
            console.error('[API Error]', error.response?.data || error.message);
        }

        // Network error (no response)
        if (!error.response) {
            console.error('Network error:', error.message);
            // Don't auto-toast network errors, let components handle them
            return Promise.reject({
                ...error,
                userMessage: 'Network error. Please check your connection'
            });
        }

        const { status, data } = error.response;


        // Handle 401/403 errors (unauthorized) - clear auth state
        if (status === 401 || status === 403) {
            localStorage.removeItem('authUser');

            // Show toast only if user was logged in (to avoid spam on public pages)
            const wasLoggedIn = localStorage.getItem('authUser');
            if (wasLoggedIn && status === 401) {
                toast.error('Session expired. Please login again');
            }

            // Redirect to login if not already on auth pages or homepage
            const currentPath = window.location.pathname;
            const authPaths = ['/', '/login', '/signup', '/forgetpassword'];
            if (!authPaths.includes(currentPath)) {
                window.location.href = '/login';
            }
        }

        // Attach user-friendly message to error
        let userMessage = data?.message || 'An error occurred';

        // If we have an error code, use the mapped message
        if (data?.code && errorMessageMap[data.code]) {
            userMessage = errorMessageMap[data.code];
        }

        // Return enhanced error
        return Promise.reject({
            ...error,
            userMessage,
            errorCode: data?.code,
            details: data?.details
        });
    }
);

// Export the API_BASE_URL and axios instance
export { api, errorMessageMap };
export default API_BASE_URL;
